import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { bugsnagClient } from "../../bugsnag";
import { useAuth } from "../../contexts/AuthContext";
import { useCreditBalance } from "../../hooks/useCreditBalance";
import { ANALYTICS_EVENTS, trackEvent } from "../../utils/analytics";
import api from "../../utils/api";
import { CreditBalanceCard } from "./CreditBalanceCard";
import { ChangePasswordSection } from "./ChangePasswordSection";
import type { NotifiableError } from "@bugsnag/core/types/common";
import type { ProfileFormData } from "./types";

const INITIAL_FORM_DATA: ProfileFormData = {
  firstName: "",
  lastName: "",
  email: "",
};

export const Profile = () => {
  const { user, logout, refetchUser } = useAuth();
  const { t } = useTranslation();

  const {
    creditBalance,
    isLoading: isCreditsLoading,
    isQuantitySurveyor,
  } = useCreditBalance();

  const [formData, setFormData] = useState<ProfileFormData>(INITIAL_FORM_DATA);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
    }
  }, [user]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    setError("");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api.updateUser(formData);
      trackEvent(ANALYTICS_EVENTS.PROFILE_UPDATED);
      setIsEditing(false);
      setError("");
      await refetchUser();
    } catch (err) {
      bugsnagClient.notify(err as NotifiableError);
      setError((err as Error).message || t("profile.failed_save"));
    }
  };

  const userInitial = useMemo(
    () => formData.firstName?.trim()?.[0]?.toUpperCase() || "U",
    [formData.firstName],
  );

  return (
    <div className="h-full from-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-4 p-6">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-blue-500 text-white text-lg font-semibold">
                {userInitial}
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">
                  {t("profile.title")}
                </h1>
                <p className="text-sm text-slate-500">
                  {t("profile.subtitle")}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 active:scale-[.99]"
            >
              {t("profile.logout")}
            </button>
          </div>
          <div className="h-px w-full bg-slate-200" />
          {error && (
            <div className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid gap-5">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {t("profile.first_name")}
                </span>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  autoComplete="firstName"
                  aria-invalid={!!error}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-indigo-500 transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {t("profile.last_name")}
                </span>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  autoComplete="lastName"
                  aria-invalid={!!error}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-indigo-500 transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {t("profile.email")}
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  autoComplete="email"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-indigo-500 transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50"
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              {!isEditing ? (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 active:scale-[.99]"
                >
                  {t("profile.edit")}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:scale-[.99]"
                  >
                    {t("profile.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 active:scale-[.99]"
                  >
                    {t("profile.save")}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
        <ChangePasswordSection />
        {isQuantitySurveyor && (
          <CreditBalanceCard
            creditBalance={creditBalance}
            isLoading={isCreditsLoading}
          />
        )}
        {isEditing && (
          <div className="sm:hidden fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
            <div className="mx-auto flex max-w-3xl items-center gap-2">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t("profile.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex-1 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500"
              >
                {t("profile.save_short")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
